import React, { useState, useEffect, useRef, useCallback } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { fetchReservations, RESERVATION_LIST_SIZE } from '../../services/api.js';
import { Avatar, ResBadge, OfflineBadge, Paginator, fmtDate, fmtCurrency,
  useOfflineInvalidation } from '../../components/ui.jsx';

// ─── Status meta ──────────────────────────────────────────────────────────────
const RES_META = {
  checked_in:  { color: '#C0392B', label: 'In-House'    },
  confirmed:   { color: '#1A7A4A', label: 'Confirmed'   },
  tentative:   { color: '#E07B00', label: 'Tentative'   },
  cancelled:   { color: '#6B7A8D', label: 'Cancelled'   },
  checked_out: { color: '#3A4A5C', label: 'Checked Out' },
  no_show:     { color: '#6B46C1', label: 'No Show'     },
};

const STATUS_TABS = [
  { id: '',            label: 'All'        },
  { id: 'checked_in',  label: 'In-House'  },
  { id: 'confirmed',   label: 'Confirmed' },
  { id: 'tentative',   label: 'Tentative' },
  { id: 'cancelled',   label: 'Cancelled' },
];

// Corporate-category filter — separate from status tabs
const CAT_ALL       = '';
const CAT_CORPORATE = 'corporate';
const CAT_PERSONAL  = 'personal';

const CAT_TABS = [
  { id: CAT_ALL,       label: 'All Types'    },
  { id: CAT_CORPORATE, label: '🏢 Corporate' },
  { id: CAT_PERSONAL,  label: '👤 Individual' },
];

// ─── Corporate badge ──────────────────────────────────────────────────────────
function CorporateBadge({ r }) {
  if (r.reservationCategory !== 'corporate') return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      marginTop: 3, flexWrap: 'wrap',
    }}>
      <span style={{
        fontSize: 9, fontWeight: 700, color: T.navy,
        background: `${T.navy}12`,
        border: `1px solid ${T.navy}30`,
        borderRadius: 5, padding: '1px 6px',
        letterSpacing: 0.5, textTransform: 'uppercase',
        lineHeight: 1.6,
      }}>
        🏢 {r.corporateAccountName || 'Corporate'}
      </span>
      {r.corporateSessionId && (
        <span style={{
          fontSize: 9, color: T.teal,
          background: `${T.teal}10`,
          border: `1px solid ${T.teal}30`,
          borderRadius: 5, padding: '1px 6px',
          letterSpacing: 0.3, lineHeight: 1.6,
        }}>
          📋 Session
        </span>
      )}
    </div>
  );
}

// ─── Single reservation row ───────────────────────────────────────────────────
function ResRow({ r, currency, onSelect }) {
  const [hov, setHov] = useState(false);
  const meta   = RES_META[r.status] || RES_META.confirmed;
  const nights = r.arrivalDate && r.departureDate
    ? Math.max(0, Math.ceil(
        (new Date(r.departureDate) - new Date(r.arrivalDate)) / 86_400_000
      ))
    : 0;
  const isCorporate = r.reservationCategory === 'corporate';

  return (
    <div
      onClick={() => onSelect(r.id)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'stretch',
        background: isCorporate ? `${T.navy}04` : T.white,
        borderRadius: 13,
        border: `1.5px solid ${hov ? meta.color + '55' : isCorporate ? `${T.navy}20` : T.border}`,
        boxShadow: hov
          ? `0 6px 22px ${meta.color}18, 0 1px 4px rgba(0,0,0,.05)`
          : SHADOW.sm,
        transform: hov ? 'translateY(-2px)' : 'none',
        transition: 'all .18s cubic-bezier(.4,0,.2,1)',
        cursor: 'pointer', overflow: 'hidden',
      }}>

      {/* Status stripe */}
      <div style={{ width: 4, background: meta.color, flexShrink: 0 }} />

      {/* Row content */}
      <div style={{
        flex: 1, display: 'grid', alignItems: 'center',
        gridTemplateColumns:
          'minmax(160px,2.5fr) minmax(110px,1.4fr) minmax(140px,2fr) 64px minmax(96px,1.1fr) 88px 58px',
        gap: 8, padding: '11px 14px', minWidth: 0,
      }}>

        {/* Guest + optional corporate badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <Avatar name={r.guestName} size={34} />
          <div style={{ minWidth: 0 }}>
            <p style={{
              fontSize: 13, fontWeight: 700, color: T.navy,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {r.guestName || '—'}
            </p>
            <p style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>
              {r.adults || 1} adult{(r.adults || 1) !== 1 ? 's' : ''}
              {r.children > 0 ? `, ${r.children} child` : ''}
            </p>
            <CorporateBadge r={r} />
            {r.appliedTariffType === 'extra_person' && (
              <span style={{
                display: 'inline-block', marginTop: 3,
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                color: '#78350F', background: '#FEF9C3',
                border: '1px solid #FDE68A',
                borderRadius: 5, padding: '1px 6px', lineHeight: 1.6,
                textTransform: 'uppercase',
              }} title={r.appliedTariffLabel || 'Extra person rate applied'}>
                👥 Extra Person
              </span>
            )}
          </div>
        </div>

        {/* Confirmation # */}
        <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: T.slate }}>
          {r.confirmationNumber || '—'}
        </span>

        {/* Dates */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: T.navy }}>
            {fmtDate(r.arrivalDate)} → {fmtDate(r.departureDate)}
          </p>
          {nights > 0 && (
            <p style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>
              {nights} night{nights !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Room */}
        <div style={{ textAlign: 'center' }}>
          {r.roomNumber
            ? <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{r.roomNumber}</span>
            : <span style={{ fontSize: 12, color: T.muted }}>—</span>}
        </div>

        {/* Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <ResBadge status={r.status} />
          <OfflineBadge item={r} />
        </div>

        {/* Amount */}
        <span style={{ fontSize: 13, fontWeight: 700, color: T.navy, textAlign: 'right' }}>
          {fmtCurrency(r.totalAmount || 0, currency)}
        </span>

        {/* View */}
        <button
          onClick={e => { e.stopPropagation(); onSelect(r.id); }}
          style={{
            background: hov ? T.navy : T.surface,
            color: hov ? T.white : T.muted,
            border: `1px solid ${hov ? T.navy : T.border}`,
            borderRadius: 8, padding: '5px 10px',
            fontSize: 11, fontWeight: 600,
            cursor: 'pointer', fontFamily: FONTS.body,
            transition: 'all .15s', whiteSpace: 'nowrap',
          }}>
          View →
        </button>
      </div>
    </div>
  );
}

// ─── Column headers ───────────────────────────────────────────────────────────
function ColHeaders() {
  const cell = {
    fontSize: 10, fontWeight: 700, color: T.muted,
    letterSpacing: 0.7, textTransform: 'uppercase',
  };
  return (
    <div style={{
      display: 'grid', alignItems: 'center',
      gridTemplateColumns:
        'minmax(160px,2.5fr) minmax(110px,1.4fr) minmax(140px,2fr) 64px minmax(96px,1.1fr) 88px 58px',
      gap: 8, padding: '0 14px 8px 18px',
    }}>
      <span style={cell}>Guest</span>
      <span style={cell}>Confirmation</span>
      <span style={cell}>Dates</span>
      <span style={{ ...cell, textAlign: 'center' }}>Room</span>
      <span style={cell}>Status</span>
      <span style={{ ...cell, textAlign: 'right' }}>Amount</span>
      <span style={cell} />
    </div>
  );
}

// ─── Active filter summary strip ──────────────────────────────────────────────
function FilterSummary({ tab, cat, search, total, onClearCat, onClearSearch }) {
  const parts = [];
  if (cat === CAT_CORPORATE) parts.push({ label: '🏢 Corporate only', onClear: onClearCat });
  if (cat === CAT_PERSONAL)  parts.push({ label: '👤 Individual only', onClear: onClearCat });
  if (search)                parts.push({ label: `Search: "${search}"`,  onClear: onClearSearch });
  if (parts.length === 0) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 12, flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 11, color: T.muted }}>Filters:</span>
      {parts.map((p, i) => (
        <span key={i} style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: 11, fontWeight: 600, color: T.teal,
          background: `${T.teal}10`, border: `1px solid ${T.teal}30`,
          borderRadius: 99, padding: '3px 10px',
        }}>
          {p.label}
          <button
            onClick={p.onClear}
            style={{
              background: 'none', border: 'none', color: T.muted,
              cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 13,
            }}>×</button>
        </span>
      ))}
      {total > 0 && (
        <span style={{ fontSize: 11, color: T.muted, marginLeft: 'auto' }}>
          {total} result{total !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReservationList({ property, onSelect, onNew }) {
  const [reservations, setRes]   = useState([]);
  const [total,        setTotal] = useState(0);
  const [loading,      setLoad]  = useState(true);
  const [statusTab,    setStatus] = useState('');
  const [catTab,       setCat]   = useState(CAT_ALL);
  const [search,       setSrch]  = useState('');
  const [page,         setPage]  = useState(0);
  const debRef = useRef(null);
  const pid    = property?.id;
  const currency = property?.currency || 'USD';

  const offlineTick = useOfflineInvalidation('reservations');

  const load = useCallback((s, status, cat, p) => {
    if (!pid) return;
    setLoad(true);
    // category is now a real query param — fetchReservations/backend filter
    // on it directly (reservationCategory column), so no more search-string
    // smuggling and no client-side fallback filter needed.
    fetchReservations(pid, status, s, p, RESERVATION_LIST_SIZE, cat)
      .then(data => {
        const items = data?.content ?? (Array.isArray(data) ? data : []);
        setRes(items);
        setTotal(data?.totalElements ?? items.length);
      })
      .catch(() => setRes([]))
      .finally(() => setLoad(false));
  }, [pid]);

  useEffect(() => {
    load(search, statusTab, catTab, page);
  }, [pid, statusTab, catTab, page, offlineTick]); // eslint-disable-line

  const handleSearch = v => {
    setSrch(v); setPage(0);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => load(v, statusTab, catTab, 0), 380);
  };

  const handleStatus = s => { setStatus(s); setPage(0); load(search, s, catTab, 0); };
  const handleCat    = c => { setCat(c);   setPage(0); load(search, statusTab, c, 0); };
  const clearCat     = () => handleCat(CAT_ALL);
  const clearSearch  = () => handleSearch('');

  return (
    <div className="fade-up">
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -500px 0; }
          100% { background-position:  500px 0; }
        }
        .res-shimmer {
          background: linear-gradient(90deg,#f0f4f8 25%,#e8edf4 50%,#f0f4f8 75%);
          background-size: 500px 100%;
          animation: shimmer 1.5s infinite;
        }
      `}</style>

      {/* ── Page header ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 22, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h1 style={{
            fontFamily: FONTS.display, fontSize: 30, fontWeight: 700,
            color: T.navy, lineHeight: 1.1,
          }}>
            Reservations
          </h1>
          <p style={{ fontSize: 12, color: T.muted, marginTop: 4, fontFamily: FONTS.body }}>
            {property?.name}{total > 0 ? ` · ${total} reservation${total !== 1 ? 's' : ''}` : ''}
          </p>
        </div>

        <button onClick={onNew} style={{
          display: 'flex', alignItems: 'center', gap: 7,
          background: T.gold, color: T.navy, border: 'none',
          borderRadius: 11, padding: '11px 22px',
          fontSize: 13, fontWeight: 800, cursor: 'pointer',
          fontFamily: FONTS.body,
          boxShadow: `0 4px 18px ${T.gold}45`,
          transition: 'all .15s',
        }}>
          + New Reservation
        </button>
      </div>

      {/* ── Category tabs (All / Corporate / Individual) ── */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap',
      }}>
        {CAT_TABS.map(t => {
          const active = catTab === t.id;
          const isCorp = t.id === CAT_CORPORATE;
          return (
            <button key={t.id} onClick={() => handleCat(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: active ? (isCorp ? `${T.navy}12` : T.navy) : T.white,
                color: active ? (isCorp ? T.navy : T.white) : T.muted,
                border: `1.5px solid ${active ? (isCorp ? `${T.navy}40` : T.navy) : T.border}`,
                borderRadius: 99, padding: '5px 14px',
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                fontFamily: FONTS.body, transition: 'all .15s',
              }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Status pills + Search ── */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'center',
        marginBottom: 14, flexWrap: 'wrap',
      }}>
        {/* Status pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
          {STATUS_TABS.map(t => {
            const meta     = t.id ? RES_META[t.id] : null;
            const isActive = statusTab === t.id;
            return (
              <button key={t.id} onClick={() => handleStatus(t.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: isActive ? (meta ? `${meta.color}12` : T.navy) : T.white,
                  color: isActive ? (meta ? meta.color : T.white) : T.muted,
                  border: `1.5px solid ${isActive
                    ? (meta ? `${meta.color}50` : T.navy)
                    : T.border}`,
                  borderRadius: 99, padding: '6px 16px',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  fontFamily: FONTS.body, transition: 'all .15s',
                }}>
                {meta && (
                  <div style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: meta.color,
                  }} />
                )}
                {t.label}
                {isActive && total > 0 ? ` · ${total}` : ''}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', flexShrink: 0, width: 268 }}>
          <span style={{
            position: 'absolute', left: 11, top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 13, color: T.muted, pointerEvents: 'none',
          }}>🔍</span>
          <input
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Guest, company, session or conf #…"
            style={{
              width: '100%', padding: '7px 12px 7px 30px',
              border: `1.5px solid ${search ? T.teal : T.border}`,
              borderRadius: 10, fontSize: 12, fontFamily: FONTS.body,
              background: T.white, color: T.navy, outline: 'none',
              transition: 'border-color .15s', boxSizing: 'border-box',
            }}
          />
          {search && (
            <button
              onClick={clearSearch}
              style={{
                position: 'absolute', right: 9, top: '50%',
                transform: 'translateY(-50%)',
                background: 'none', border: 'none',
                color: T.muted, cursor: 'pointer',
                fontSize: 15, lineHeight: 1, padding: 0,
              }}>×</button>
          )}
        </div>
      </div>

      {/* Active filter summary */}
      <FilterSummary
        tab={statusTab} cat={catTab} search={search} total={total}
        onClearCat={clearCat} onClearSearch={clearSearch}
      />

      {/* ── List ── */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="res-shimmer"
              style={{ height: 62, borderRadius: 13 }} />
          ))}
        </div>
      ) : reservations.length === 0 ? (
        <div style={{
          background: T.white, border: `2px dashed ${T.border}`,
          borderRadius: 16, padding: '52px 24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>
            {catTab === CAT_CORPORATE ? '🏢' : '📋'}
          </div>
          <p style={{
            fontFamily: FONTS.display, fontSize: 22, fontWeight: 700,
            color: T.navy, marginBottom: 6,
          }}>
            {search
              ? `No results for "${search}"`
              : catTab === CAT_CORPORATE
                ? 'No corporate reservations'
                : 'No reservations here yet'}
          </p>
          <p style={{ fontSize: 13, color: T.muted, marginBottom: 22 }}>
            {search
              ? 'Try a different name, company, session or confirmation number'
              : catTab === CAT_CORPORATE
                ? 'Corporate reservations are created from the Corporate Session module'
                : 'Walk-ins, phone bookings, or online — create your first reservation'}
          </p>
          {search || catTab ? (
            <button onClick={() => { clearSearch(); clearCat(); }} style={{
              background: T.surface, color: T.slate, border: `1px solid ${T.border}`,
              borderRadius: 9, padding: '8px 18px', fontSize: 12,
              cursor: 'pointer', fontFamily: FONTS.body,
            }}>Clear filters</button>
          ) : (
            <button onClick={onNew} style={{
              background: T.gold, color: T.navy, border: 'none',
              borderRadius: 11, padding: '11px 24px',
              fontSize: 13, fontWeight: 800, cursor: 'pointer',
              fontFamily: FONTS.body,
            }}>+ New Reservation</button>
          )}
        </div>
      ) : (
        <>
          <ColHeaders />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {reservations.map(r => (
              <ResRow key={r.id} r={r} currency={currency} onSelect={onSelect} />
            ))}
          </div>
        </>
      )}

      {/* ── Paginator ── */}
      {!loading && total > RESERVATION_LIST_SIZE && (
        <div style={{ marginTop: 18 }}>
          <Paginator
            page={page} total={total}
            size={RESERVATION_LIST_SIZE}
            onChange={p => setPage(p)}
          />
        </div>
      )}
    </div>
  );
}